import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { TradableAreaRegistrationIssue_MissingMaster } from '@ebalance/proto/bffuser/v1/tradable_area_service_pb'

import type { IssuerOption } from '~/components/bidding/common/bidIssuer'
import { issuerAreaAvailability } from '~/components/bidding/common/bidIssuer'
import type { BidCsvImportIssuerCount } from '~/models/bidCsvImportReturn'
import type { BidIssuerType } from '~/models/bidIssuerType'
import { bidIssuerTypeOf } from '~/models/bidIssuerType'

/**
 * 画面上部のツールバーで選んだ発注主体区分が決める範囲 (#10119)。
 *
 * - 入札一覧に出す札 ([[isInPageIssuerScope]]): AC-BSP-019 ※ / AC-BSP-001a
 * - エリアフィルタで選べるエリア ([[areaFilterAvailability]]): AC-BSP-019 / AC-BSP-005b
 */

/**
 * 札が、ツールバーで選んだ発注主体区分の入札一覧に属するか。 通常入札の行もブロックの
 * グループも [[BidIssuerType]] で比べる。
 *
 * - ツールバーが未選択 (UNSPECIFIED) は絞り込まない
 * - 札の区分が空欄 ('unknown'。 空欄の警告 [[isUnknownIssuerLicenseType]] と同じ判定) は、
 *   選んだ区分に関わらず属する。 どの区分を選んでも見えない状態を作らない (AC-BSP-111b)
 * - 札の区分が未供給 (undefined) も属する。 区分が分からない札を隠さない
 */
export const isInPageIssuerScope = (
  bidIssuerType: BidIssuerType | undefined,
  pageIssuerType: LicenseType,
): boolean =>
  pageIssuerType === LicenseType.UNSPECIFIED ||
  bidIssuerType === undefined ||
  bidIssuerType === 'unknown' ||
  bidIssuerType === bidIssuerTypeOf(pageIssuerType)

/**
 * 入札 CSV で取り込んだ札の区分別の件数のうち、 ツールバーで選んだ発注主体区分の入札一覧に
 * 出ない区分の分 (AC-SPOT-024。 取込完了のトーストに区分名と件数を入れて知らせる)。
 * 一覧に出るかの判定は [[isInPageIssuerScope]] と同じ (区分が空欄の札は選んだ区分に関わらず出る)。
 */
export const issuerCountsOutsidePageScope = (
  byIssuer: readonly BidCsvImportIssuerCount[],
  pageIssuerType: LicenseType,
): BidCsvImportIssuerCount[] =>
  byIssuer.filter(
    ({ issuerType }) =>
      !isInPageIssuerScope(bidIssuerTypeOf(issuerType), pageIssuerType),
  )

/** エリア未選択では区分不明の取り込み札を区分別の取引可能エリアで隠さない (AC-BSP-111b)。 */
export const isInPageAreaScope = (
  area: AreaCode,
  issuerType: BidIssuerType | undefined,
  selectedAreas: ReadonlySet<AreaCode>,
  unknownIssuerUnselectedAreas: readonly AreaCode[] | undefined,
): boolean =>
  selectedAreas.has(area) ||
  (issuerType === 'unknown' &&
    unknownIssuerUnselectedAreas?.includes(area) === true)

/**
 * 画面上部のツールバーの絞り込み (エリア・発注主体区分) が決める入札一覧の範囲。 一括取下 (AC-BSP-116) が
 * 両タブの一覧と同じ範囲で対象を数えるのに使う。
 */
export type PageListScope = {
  selectedAreas: ReadonlySet<AreaCode>
  unknownIssuerUnselectedAreas: readonly AreaCode[] | undefined
  issuerScopeType: LicenseType
}

/**
 * 閲覧用エリアフィルタの選択可否。 登録可否は発注可否 ([[issuerAreaAvailability]]) とは分ける
 * (AC-IJM-013)。 区分が決まらない間だけ組織全体の未登録理由を使う。 業務エリア設定に
 * 含まれないエリアは `outsideBusinessAreas` (AC-BSP-005b)。
 */
export type AreaFilterAvailability =
  | 'selectable'
  | 'unregistered'
  | 'orgUnregistered'
  | 'outsideBusinessAreas'

/**
 * 入札一覧のエリアフィルタで、 `area` を選べるか (選べない時の理由の種別つき)。
 *
 * ツールバーで区分を選んでいれば、その区分の登録集合で決める。 会員情報・受渡契約コードが
 * 無く発注できなくても、登録済みエリアの一覧とチャートは閲覧できる (AC-BSP-019 / AC-IJM-013)。
 * 未選択・申請セットの確認中は全区分の登録集合を使う。 札を作るモーダルの選択肢には使わない。
 * 登録済みでも業務エリア設定に含まれなければ選べない。 両方に当たる時は未登録の理由を返す
 * (AC-BSP-005b。 登録が業務エリア設定より根本の制約のため)。
 */
export const areaFilterAvailability = (
  pageIssuer: IssuerOption | undefined,
  orgUnregisteredAreas: ReadonlySet<AreaCode>,
  businessAreaCodes: readonly AreaCode[],
  area: AreaCode,
): AreaFilterAvailability => {
  const registration = areaFilterRegistration(
    pageIssuer,
    orgUnregisteredAreas,
    area,
  )
  if (registration !== 'selectable') return registration
  return businessAreaCodes.includes(area)
    ? 'selectable'
    : 'outsideBusinessAreas'
}

const areaFilterRegistration = (
  pageIssuer: IssuerOption | undefined,
  orgUnregisteredAreas: ReadonlySet<AreaCode>,
  area: AreaCode,
): 'selectable' | 'unregistered' | 'orgUnregistered' => {
  if (pageIssuer !== undefined) {
    return pageIssuer.registeredAreas.some(
      (registered) => registered.area === area,
    )
      ? 'selectable'
      : 'unregistered'
  }
  return orgUnregisteredAreas.has(area) ? 'orgUnregistered' : 'selectable'
}

/**
 * 受渡契約コードを解決できる (= 広域機関に登録済みの) エリア (AC-BSP-105 (b) の判定)。
 *
 * ツールバーで区分を選んでいれば、 その区分の登録済みエリア。 未選択のうちは組織の全区分
 * (`orgIssuers`) を合わせた範囲。
 */
export const registeredAreasInScope = (
  pageIssuer: IssuerOption | undefined,
  orgIssuers: ReadonlyArray<{
    registeredAreas: ReadonlyArray<{ area: AreaCode }>
  }>,
): ReadonlySet<AreaCode> =>
  new Set(
    (pageIssuer !== undefined ? [pageIssuer] : orgIssuers).flatMap((issuer) =>
      issuer.registeredAreas.map((r) => r.area),
    ),
  )

/** (b) の {マスタ名} の並び (AC-BSP-105 (b): 計画提出者 → 需要調達計画 → BG)。 */
const MISSING_MASTER_ORDER = [
  TradableAreaRegistrationIssue_MissingMaster.SUBMITTER,
  TradableAreaRegistrationIssue_MissingMaster.DEMAND_PROCUREMENT,
  TradableAreaRegistrationIssue_MissingMaster.BG,
] as const

/**
 * 空状態 (b) の {マスタ名} に出す、 登録が足りないマスタ (AC-BSP-105 (b) / AC-IJM-012)。
 * `pageIssuer` の範囲 (未選択なら組織の発注主体区分 `orgIssuerTypes`) の JEPX 取引対象エリアについて、
 * backend が返した不足の理由を spec の並びで重複なく返す。 理由が分からない値 (未指定・未知) は推測せず除く。
 *
 * 未選択の間も `orgIssuerTypes` で絞るのは、 backend が組織に無い区分の不足も返すため。 絞らないと、
 * 組織が持たない区分のマスタへの登録を案内してしまう。
 */
export const missingMastersInScope = (
  pageIssuer: IssuerOption | undefined,
  registrationIssues: ReadonlyArray<{
    issuerType: LicenseType
    area: AreaCode
    missingMaster: TradableAreaRegistrationIssue_MissingMaster
  }>,
  jepxAreaCodes: readonly AreaCode[],
  orgIssuerTypes: readonly LicenseType[],
): TradableAreaRegistrationIssue_MissingMaster[] => {
  const missing = new Set(
    registrationIssues
      .filter(
        (issue) =>
          (pageIssuer === undefined
            ? orgIssuerTypes.includes(issue.issuerType)
            : issue.issuerType === pageIssuer.issuerType) &&
          jepxAreaCodes.includes(issue.area),
      )
      .map((issue) => issue.missingMaster),
  )
  return MISSING_MASTER_ORDER.filter((master) => missing.has(master))
}

/**
 * 「選べるエリアが 0 件」の空状態 (AC-BSP-105) の判定。 `pageIssuer` の範囲 (未選択なら組織全体) で、
 * 選べるエリア = 業務エリア ∩ その範囲で広域機関に登録済みの JEPX 取引対象エリア が 0 件かを求める。
 * 会員情報の未登録は条件にしない (AC-BSP-105 / AC-IJM-013)。
 *
 * - `orgIssuers` が未確定 (undefined) の間は、 登録済みエリアを絞り込まない側に倒し、 0 件と確定させない
 * - 発注主体区分が 0 件 (AC-BSP-005d) の時は、 判定順により空状態パネルを出さない
 * - 業務エリアに JEPX 取引対象が 1 つも無い (`hasNoAreaCandidate`) 時は、 パネルを出さない
 *   状態でも一覧・チャート・エリア別合計を出さない (`hidesAreaContent`)。 Des 判断 #7502
 *   (2026-09-09): エリアラベルだけを残さず、 東京などを暗黙に選択した一覧・チャートは表示しない。
 *   候補が空だと選択中エリアも空になり、 描画側で止めないと 0 件の一覧や空のチャート枠が出る
 */
export const areaEmptyStateInScope = ({
  areaCodes,
  jepxAreaCodes,
  orgIssuers,
  registeredIssuerCount,
  pageIssuer,
}: {
  areaCodes: readonly AreaCode[]
  jepxAreaCodes: readonly AreaCode[]
  orgIssuers:
    | ReadonlyArray<{ registeredAreas: ReadonlyArray<{ area: AreaCode }> }>
    | undefined
  registeredIssuerCount: number | undefined
  pageIssuer: IssuerOption | undefined
}): {
  orderableAreas: readonly AreaCode[]
  isBroadAreaMasterEmpty: boolean
  showsAreaEmptyPanel: boolean
  hasNoAreaCandidate: boolean
  hidesAreaContent: boolean
} => {
  const registered =
    orgIssuers === undefined
      ? undefined
      : registeredAreasInScope(pageIssuer, orgIssuers)
  const orderableAreas =
    registered === undefined
      ? jepxAreaCodes
      : jepxAreaCodes.filter((code) => registered.has(code))
  const isBroadAreaMasterEmpty = orderableAreas.length === 0
  const hasNoIssuer = registeredIssuerCount === 0
  const hasSelectableArea = areaCodes.some((code) =>
    orderableAreas.includes(code),
  )
  const showsAreaEmptyPanel = !hasSelectableArea && !hasNoIssuer
  const hasNoAreaCandidate = areaCodes.length === 0
  return {
    orderableAreas,
    isBroadAreaMasterEmpty,
    showsAreaEmptyPanel,
    hasNoAreaCandidate,
    hidesAreaContent: showsAreaEmptyPanel || hasNoAreaCandidate,
  }
}

/**
 * 選択中の発注主体区分とエリアの絞り込みに関わらず、 入札一覧に出す下書きか (spec AC-BSP-019)。
 * 保存済みの区分がその受渡日の選択肢に無い下書き (AC-BSP-135 で区分を選び直す) と、 札のエリア
 * (ループは A か B のどちらか) をどの区分でも広域機関に登録していない下書き (作り直すしかない) で、
 * 一覧から見えなくなると直す入口が無くなるため。
 *
 * `options` はその受渡日の区分の選択肢で、 取得できるまで (取得中・取得失敗) は undefined を渡す。
 * 取得できない間は選択肢が空に縮退するので、 当てはめると全ての下書きが出てしまう。 区分が未供給
 * (undefined) の札は、 区分が分からないので当てはめない。
 */
export const isAlwaysListedDraft = (
  draft: {
    isDraft: boolean
    issuerType: BidIssuerType | undefined
    areas: readonly AreaCode[]
  },
  options: readonly IssuerOption[] | undefined,
): boolean => {
  if (!draft.isDraft || options === undefined) return false
  if (draft.issuerType === undefined) return false
  const isIssuerInOptions = options.some(
    (option) => bidIssuerTypeOf(option.issuerType) === draft.issuerType,
  )
  if (!isIssuerInOptions) return true
  return draft.areas.some((area) =>
    options.every(
      (option) => issuerAreaAvailability(option, area) === 'unregistered',
    ),
  )
}
